import styled from 'styled-components';
import { mediaMMdMin } from 'helpers/breakpoints';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getBoatPhotoUrls } from 'helpers';
import NextImageWithShimmer, { getPathnameFromUrl } from 'components/image/NextImageWithShimmer';
import { useScrollSnap } from 'hooks/useScrollSnap';

// To be used in the search map to keep the boat card and map marker image the same
// sizes conditions must be ordered from most-restrictive (smallest max-width)
// to least-restrictive so the browser picks the first matching condition.
export const plpGallerySizes =
  '(max-width: 769px) calc(100vw - 42px - 42px), (max-width: 959px) calc(100vw / 2 - 42px - 42px), (max-width: 1152px) calc(100vw / 3 - 42px - 42px), (max-width: 1919px) calc(calc(100vw - 32vw) / 2 - 24px - 42px), (max-width: 2559px) calc(calc(100vw - 32vw) / 3 - 24px - 42px), calc(100vw - 22px - 22px)';

interface Photo {
  standard?: { url?: string };
  original?: { url?: string };
}

interface GalleryProps {
  photos: Photo[];
  cardIndex: number;
  onChange: (activeIndex: number) => void;
  boatTitle?: string;
}
export default function Gallery({ photos, cardIndex, onChange, boatTitle }: GalleryProps) {
  const photoUrls = getBoatPhotoUrls(photos);
  const { scrollRef, scrollToPrev, scrollToNext } = useScrollSnap();
  const [activeIndex, setActiveIndex] = useState(0);
  const prevIndexRef = useRef(0);

  const getCurrentIndex = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return 0;

    const slides = container.querySelectorAll<HTMLElement>('[data-slide-index]');
    if (!slides.length) return 0;

    if (container.scrollLeft + container.clientWidth >= container.scrollWidth - 2) {
      return slides.length - 1;
    }

    const containerRect = container.getBoundingClientRect();
    let closestIndex = 0;
    let closestDistance = Infinity;

    slides.forEach((slide, i) => {
      const distance = Math.abs(slide.getBoundingClientRect().left - containerRect.left);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = i;
      }
    });

    return closestIndex;
  }, [scrollRef]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return undefined;

    const handleScroll = () => {
      setActiveIndex(getCurrentIndex());
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [getCurrentIndex, scrollRef]);

  // Fire analytics on slide change
  useEffect(() => {
    if (activeIndex !== prevIndexRef.current) {
      prevIndexRef.current = activeIndex;
      onChange(activeIndex);
    }
  }, [activeIndex, onChange]);

  return (
    <GalleryWrapper data-testid="search-boat-card-photo-swiper">
      <ScrollContainer ref={scrollRef}>
        {photoUrls.map((url, i) => (
          <Slide key={String(url)} data-slide-index={i}>
            <NextImageWithShimmer
              src={getPathnameFromUrl(url)}
              priority={i === 0 && cardIndex < 3}
              fill
              alt={boatTitle || 'Boat rental'}
              sizes={plpGallerySizes}
              style={{
                objectFit: 'cover',
              }}
            />
          </Slide>
        ))}
      </ScrollContainer>

      {photoUrls.length > 1 && (
        <>
          <StyledArrowPrevIcon
            className="prev"
            data-testid="search-boat-card-photo-swiper-prev"
            data-disabled={activeIndex === 0 || undefined}
            onClick={(e) => {
              e.stopPropagation();
              scrollToPrev();
            }}
          />
          <StyledArrowNextIcon
            className="next"
            data-testid="search-boat-card-photo-swiper-next"
            data-disabled={activeIndex === photoUrls.length - 1 || undefined}
            onClick={(e) => {
              e.stopPropagation();
              scrollToNext();
            }}
          />
          <PaginationFraction>
            {activeIndex + 1} / {photoUrls.length}
          </PaginationFraction>
        </>
      )}
    </GalleryWrapper>
  );
}

const GalleryWrapper = styled.div`
  position: relative;
  height: 256px;

  &:hover {
    .next,
    .prev {
      &:not([data-disabled='true']) {
        opacity: 1;
      }
      &[data-disabled='true'] {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }
  }
`;

const ScrollContainer = styled.div`
  display: flex;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  height: 100%;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Slide = styled.div`
  flex-shrink: 0;
  scroll-snap-align: start;
  scroll-snap-stop: always;
  position: relative;
  width: 100%;
  height: 100%;
`;

const PaginationFraction = styled.div`
  position: absolute;
  z-index: 1;
  left: 18px;
  bottom: 14px;
  background: #17233ca1;
  border-radius: 34px;
  color: #fff;
  font-size: 10px;
  font-weight: 600;
  line-height: 19px;
  padding: 0 9px;
`;

export const ArrowNextIcon = (props) => (
  <svg width={29} height={29} fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <circle cx={14.5} cy={14.5} r={14.5} fill="#F5F7F9" />
    <path
      d="m18.125 14.5-4.66-4.406a.478.478 0 0 0-.645 0 .417.417 0 0 0 0 .61l4.015 3.796-4.015 3.796a.417.417 0 0 0 0 .61.478.478 0 0 0 .645 0l4.66-4.406Z"
      fill="#0751C2"
    />
    <path
      d="m18.125 14.5.343.363a.5.5 0 0 0 0-.726l-.343.363Zm-4.66-4.406-.344.363.344-.363Zm-.645.61-.344.363.344-.363Zm4.015 3.796.344.363.384-.363-.384-.363-.344.363Zm-4.015 3.796-.344-.363.344.363Zm.645.61.343.364-.343-.364Zm5.003-4.77-4.66-4.405-.687.726 4.66 4.406.688-.726Zm-4.66-4.405a.978.978 0 0 0-1.332 0l.687.726a.049.049 0 0 1-.015.01.017.017 0 0 1-.006.002l-.005-.002a.049.049 0 0 1-.016-.01l.687-.726Zm-1.332 0a.917.917 0 0 0 0 1.336l.687-.727a.083.083 0 0 1 0 .117l-.687-.726Zm0 1.336 4.016 3.796.687-.726-4.016-3.797-.687.727Zm4.016 3.07-4.016 3.796.687.727 4.016-3.797-.687-.726Zm-4.016 3.796a.917.917 0 0 0 0 1.337l.687-.727a.083.083 0 0 1 0 .117l-.687-.727Zm0 1.337a.978.978 0 0 0 1.332 0l-.687-.727a.049.049 0 0 1 .016-.01.018.018 0 0 1 .005-.002l.006.002c.003 0 .01.004.015.01l-.687.727Zm1.332 0 4.66-4.407-.686-.726-4.66 4.406.686.727Z"
      fill="#0751C2"
    />
  </svg>
);

const StyledArrowNextIcon = styled(ArrowNextIcon)`
  position: absolute;
  z-index: 1;
  top: 50%;
  right: 10px;
  left: unset;
  transform: translateY(-50%);
  cursor: pointer;
  transition: opacity 0.3s ease-in-out;
  display: none;

  ${mediaMMdMin} {
    opacity: 0;
    display: block;
  }

  &[data-disabled='true'] {
    pointer-events: none;
  }
`;

const StyledArrowPrevIcon = styled(StyledArrowNextIcon)`
  left: 10px;
  right: unset;
  transform: translateY(-50%) rotate(180deg);
`;
